import { WB_W, WB_H, bbox } from './whiteboardShapes';
import { ROTATES_BY_FIELD } from './wbGeometry';

const CURSOR = { nw: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', sw: 'nesw-resize', n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize' };

// Visible dot + a larger invisible ring so fingers can grab it on phones.
function Handle({ id, x, y, r, hitR, scale, onDown, cursor = 'pointer', fill = '#FFFFFF' }) {
  return (
    <g data-testid={`wb-handle-${id}`} onPointerDown={(e) => onDown(e, id)} style={{ cursor }}>
      <circle cx={x} cy={y} r={hitR || r} fill="transparent" />
      <circle cx={x} cy={y} r={r} fill={fill} stroke="#2563EB" strokeWidth={2 * scale} />
    </g>
  );
}

// Conditional SVG children are components that return null (see whiteboardShapes.jsx note).
function RotateHandle({ show, cx, top, bottom, scale, r, hitR, onDown }) {
  if (!show) return null;
  const flip = top - 30 * scale - r < 0; // too close to the top edge: put the handle below so it stays grabbable
  const from = flip ? bottom : top, y = flip ? bottom + 30 * scale : top - 30 * scale;
  return (
    <g>
      <line x1={cx} y1={from} x2={cx} y2={y} stroke="#2563EB" strokeWidth={1.5 * scale} pointerEvents="none" />
      <Handle id="rotate" x={cx} y={y} r={r} hitR={hitR} scale={scale} onDown={onDown} cursor="grab" fill="#DBEAFE" />
    </g>
  );
}

function LineHandles({ o, show, hp }) {
  if (!show) return null;
  return [<Handle key="p1" id="p1" x={o.x1} y={o.y1} cursor="move" {...hp} />, <Handle key="p2" id="p2" x={o.x2} y={o.y2} cursor="move" {...hp} />];
}

function BoxHandles({ show, L, T, R, B, edges, clamp, hp }) {
  if (!show) return null;
  const mx = (L + R) / 2, my = (T + B) / 2;
  const pts = { nw: [L, T], ne: [R, T], sw: [L, B], se: [R, B], ...(edges ? { n: [mx, T], s: [mx, B], e: [R, my], w: [L, my] } : {}) };
  return Object.entries(pts).map(([id, [x, y]]) => <Handle key={id} id={id} x={clamp.x(x)} y={clamp.y(y)} cursor={CURSOR[id]} {...hp} />);
}

// The middle of the selected object always drags it, even when handle grab-rings would overlap it (small objects).
function BodyZone({ show, b, inset, onDown }) {
  if (!show) return null;
  const ix = Math.min(inset, b.w / 4), iy = Math.min(inset, b.h / 4);
  return <rect data-testid="wb-selection-body" x={b.x + ix} y={b.y + iy} width={Math.max(1, b.w - 2 * ix)} height={Math.max(1, b.h - 2 * iy)}
    fill="transparent" style={{ cursor: 'move' }} onPointerDown={onDown} />;
}

// Single editable object: its own (rotated) frame with resize + rotate handles; lines get endpoint handles.
// Multi-selection: the union box with one rotate handle that turns everything about its centre.
export function SelectionLayer({ sb, o, multi, locked, scale, onHandleDown, onBodyDown }) {
  if (!sb) return null;
  const pad = 6 * scale, r = 9 * scale;
  const isLine = !!o && (o.type === 'line' || o.type === 'arrow');
  const b = o && !isLine ? bbox(o) : sb;
  const rot = o && ROTATES_BY_FIELD.has(o.type) ? o.rotation || 0 : 0;
  // Finger-sized grab rings, but never so big that they cover a small object's middle (that area must still move it).
  const hitR = Math.max(r, Math.min(r * 2.4, Math.min(b.w, b.h) / 3));
  const hp = { r, hitR, scale, onDown: onHandleDown };
  const L = b.x - pad, T = b.y - pad, R = b.x + b.w + pad, B = b.y + b.h + pad;
  // Keep handles of unrotated objects that touch the board edge inside the visible canvas.
  const clamp = rot ? { x: (v) => v, y: (v) => v }
    : { x: (v) => Math.max(r, Math.min(WB_W - r, v)), y: (v) => Math.max(r, Math.min(WB_H - r, v)) };
  return (
    <g data-testid="wb-selection" data-locked={locked ? 'true' : 'false'} data-rotation={rot || 0}
      transform={rot ? `rotate(${rot} ${b.x + b.w / 2} ${b.y + b.h / 2})` : undefined}>
      <rect x={L} y={T} width={R - L} height={B - T} fill="none" stroke={locked ? '#D97706' : '#2563EB'} strokeWidth={1.5 * scale}
        strokeDasharray={`${5 * scale} ${4 * scale}`} pointerEvents="none" />
      <LineHandles o={o} show={isLine && !locked} hp={hp} />
      <BoxHandles show={!!o && !isLine && !locked} L={L} T={T} R={R} B={B} edges={o?.type !== 'text'} clamp={clamp} hp={hp} />
      <RotateHandle show={!locked && (!!o || multi)} cx={(L + R) / 2} top={T} bottom={B} scale={scale} r={r} hitR={hitR} onDown={onHandleDown} />
      <BodyZone show={!!o && !isLine && !locked} b={b} inset={hitR} onDown={onBodyDown} />
    </g>
  );
}
